import { useEffect, useMemo, useState } from "react";
import AdminTopbar from "../../components/topbars/AdminTopbar";
import { IconSearch } from "../../components/icons/IconsAdmin";
import { createServicio, getServicios, updateServicio } from "../../api/apiResources";
import ServiciosForm from "./ServiciosForm";
import "./Servicios.css";

const TIPO_SERVICIO_LABELS = {
    ESPECIALIDAD: "Especialidad",
    PRACTICA: "Práctica",
};

const formatCosto = (costo) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(costo || 0);

const normalizeServicio = (servicio) => {
    return {
        id: servicio.id || servicio._id,
        nombre: servicio.nombre || servicio.name || servicio.descripcion || "Sin nombre",
        descripcion: servicio.descripcion || servicio.description || "",
        tipoServicio: servicio.tipoServicio || "",
        duracionEnMinutos: servicio.duracionEnMinutos ?? null,
        costoConsulta: servicio.costoConsulta ?? null,
        activo: servicio.activo ?? servicio.active ?? true,
        raw: servicio,
    };
};

const Servicios = () => {
    const [servicios, setServicios] = useState([]);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [drawerMode, setDrawerMode] = useState(null);
    const [selectedServicio, setSelectedServicio] = useState(null);

    const loadServicios = async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getServicios();
            setServicios(data.map(normalizeServicio));
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadServicios();
    }, []);

    const filteredServicios = useMemo(() => {
        return servicios.filter((servicio) => {
            const matchesQuery = servicio.nombre.toLowerCase().includes(query.toLowerCase());

            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "active" && servicio.activo) ||
                (statusFilter === "inactive" && !servicio.activo);

            return matchesQuery && matchesStatus;
        });
    }, [servicios, query, statusFilter]);

    const openCreateDrawer = () => {
        setSelectedServicio(null);
        setDrawerMode("create");
    };

    const openEditDrawer = (servicio) => {
        setSelectedServicio(servicio);
        setDrawerMode("edit");
    };

    const closeDrawer = () => {
        setDrawerMode(null);
        setSelectedServicio(null);
    };

    const handleSubmit = async (payload) => {
        setSaving(true);
        setError("");

        try {
            if (drawerMode === "edit" && selectedServicio?.id) {
                await updateServicio(selectedServicio.id, payload);
            } else {
                await createServicio(payload);
            }

            closeDrawer();
            await loadServicios();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <AdminTopbar
                title="Servicios"
                subtitle="Gestioná los servicios disponibles."
                actionLabel="+ Nuevo servicio"
                onAction={openCreateDrawer}
            />

            <section className="servicios-card">
                <div className="servicios-card__header">
                    <div>
                        <h2>Listado de servicios</h2>
                        <p>{filteredServicios.length} resultados encontrados</p>
                    </div>
                </div>

                <div className="servicios-toolbar">
                    <label className="servicios-search">
                        <span><IconSearch /></span>
                        <input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Buscar servicio..."
                        />
                    </label>

                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>
                </div>

                {error && <div className="servicios-error">{error}</div>}

                {loading ? (
                    <div className="servicios-empty">
                        <div className="servicios-loader" />
                        <p>Cargando servicios...</p>
                    </div>
                ) : filteredServicios.length === 0 ? (
                    <div className="servicios-empty">
                        <strong>No hay servicios para mostrar</strong>
                        <p>Creá un nuevo servicio o modificá los filtros.</p>
                    </div>
                ) : (
                    <div className="servicios-table-wrapper">
                        <table className="servicios-table">
                            <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Descripción</th>
                                <th>Tipo</th>
                                <th>Duración</th>
                                <th>Costo</th>
                                <th>Estado</th>
                                <th className="align-right">Acciones</th>
                            </tr>
                            </thead>

                            <tbody>
                            {filteredServicios.map((servicio) => (
                                <tr key={servicio.id}>
                                    <td>
                                        <strong>{servicio.nombre}</strong>
                                    </td>

                                    <td>{servicio.descripcion || "Sin descripción"}</td>

                                    <td>{TIPO_SERVICIO_LABELS[servicio.tipoServicio] || "—"}</td>

                                    <td>{servicio.duracionEnMinutos ? `${servicio.duracionEnMinutos} min` : "—"}</td>

                                    <td>{servicio.costoConsulta != null ? formatCosto(servicio.costoConsulta) : "—"}</td>

                                    <td>
                                            <span className={`servicios-badge ${servicio.activo ? "active" : "inactive"}`}>
                                                {servicio.activo ? "Activo" : "Inactivo"}
                                            </span>
                                    </td>

                                    <td className="align-right">
                                        <button
                                            className="servicios-action"
                                            type="button"
                                            onClick={() => openEditDrawer(servicio)}
                                        >
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {drawerMode && (
                <div className="servicios-drawer-overlay">
                    <aside className="servicios-drawer">
                        <ServiciosForm
                            initialValue={selectedServicio}
                            onCancel={closeDrawer}
                            onSubmit={handleSubmit}
                            saving={saving}
                        />
                    </aside>
                </div>
            )}
        </>
    );
};

export default Servicios;